// The one app store (CLAUDE.md: style state). Holds the current style, the
// active project, the theme and where you left off. Hydrates from Dexie at
// boot; every change writes back through the settings repository.

import { create } from 'zustand';
import { reportWriteFailure } from '../lib/writeGuard';
import type {
  CompTrainerState, Continuity, EqTrainerState, LibraryContinuity, LteGoal, LteSource, Settings, StyleId, TabId, Theme,
} from '../data/types';
import { leaveProject } from '../features/beat/journalStore';
import {
  DEFAULT_CONTINUITY, DEFAULT_SETTINGS, loadContinuity, loadSettings, saveContinuity, saveSettings,
} from '../db/repos/settings';

export interface ToastSpec {
  id: number;
  message: string;
  action?: { label: string; run: () => void };
  /** ms; default 4000 (DESIGN_SYSTEM §3) */
  duration?: number;
}

interface AppState {
  hydrated: boolean;
  settings: Settings;
  continuity: Continuity;
  toasts: ToastSpec[];

  hydrate: () => Promise<void>;
  setStyle: (styleId: StyleId) => void;
  /** The beat being made (SPEC F7). Persists across relaunch; undefined = none. */
  setActiveProject: (id: string | undefined) => void;
  setTheme: (theme: Theme) => void;
  /** Learn > style sheet Build/Mix toggle (SPEC F2). */
  setLearnMode: (learnMode: Settings['learnMode']) => void;
  /** Dig: include records heard in the last 90 days (SPEC F6). */
  setRediscover: (rediscover: boolean) => void;
  /** Dig source, picked genres, Include new recordings and the pinned channel (SPEC F6, v1.1). */
  setDigPrefs: (patch: Partial<Pick<Settings, 'digSource' | 'digGenres' | 'includeNew' | 'pinnedChannel'>>) => void;
  visit: (tab: TabId, path: string) => void;
  rememberScroll: (path: string, top: number) => void;
  showToast: (toast: Omit<ToastSpec, 'id'>) => number;
  dismissToast: (id: number) => void;
  /** Learn > Low End Theory builder (SPEC F2). */
  setBuilder: (source: LteSource, goal: LteGoal, forStyle: StyleId) => void;
  /** Learn > Tempo & FX calculator (SPEC F2). */
  setTempoBpm: (bpm: number) => void;
  /** The key-by-ear drone's own volume (SPEC F14). */
  setDroneLevel: (level: number) => void;
  /** The session whose check-out was just shown, answered or skipped, so it never shows twice (SPEC F10). */
  setCheckoutSeen: (sessionId: string) => void;
  /** Learn > Low End Theory note finder (SPEC F2). */
  setTuner: (note: number, oct: number) => void;
  /** Learn > EQ trainer (SPEC F15). Drags save after 400 ms; `immediate` on a committed change. */
  setEqTrainer: (next: EqTrainerState, immediate?: boolean) => void;
  /** Learn > Compressor trainer (SPEC F16 v0.3.1). Drags save after 400 ms; `immediate` on a committed change. */
  setCompTrainer: (next: CompTrainerState, immediate?: boolean) => void;
  /** Drums > Library (SPEC F3, v0.4): the chip, sheet filters and sort. */
  setLibrary: (next: LibraryContinuity) => void;
}

let toastSeq = 0;
let scrollTimer: ReturnType<typeof setTimeout> | undefined;
let eqTimer: ReturnType<typeof setTimeout> | undefined;
let compTimer: ReturnType<typeof setTimeout> | undefined;

// Writes are fire-and-forget: the UI never waits on IndexedDB.
function persist(write: Promise<void>) {
  write.catch((err: unknown) => reportWriteFailure(err));
}

export const useAppStore = create<AppState>()((set, get) => ({
  hydrated: false,
  settings: DEFAULT_SETTINGS,
  continuity: DEFAULT_CONTINUITY,
  toasts: [],

  hydrate: async () => {
    try {
      const [settings, continuity] = await Promise.all([loadSettings(), loadContinuity()]);
      set({ settings, continuity, hydrated: true });
    } catch (err) {
      // Private mode or blocked storage: run on defaults rather than a blank screen.
      console.warn('Storage unavailable, using defaults', err);
      set({ hydrated: true });
    }
  },

  setStyle: (styleId) => {
    set((s) => ({ settings: { ...s.settings, styleId } }));
    persist(saveSettings({ styleId }));
  },

  setActiveProject: (activeProjectId) => {
    // Another project, or none: the session in the one you leave is over (SPEC F10)
    if (get().settings.activeProjectId !== activeProjectId) leaveProject();
    set((s) => ({ settings: { ...s.settings, activeProjectId } }));
    persist(saveSettings({ activeProjectId }));
  },

  setTheme: (theme) => {
    set((s) => ({ settings: { ...s.settings, theme } }));
    persist(saveSettings({ theme }));
  },

  setLearnMode: (learnMode) => {
    set((s) => ({ settings: { ...s.settings, learnMode } }));
    persist(saveSettings({ learnMode }));
  },

  setRediscover: (rediscover) => {
    set((s) => ({ settings: { ...s.settings, rediscover } }));
    persist(saveSettings({ rediscover }));
  },

  setDigPrefs: (patch) => {
    set((s) => ({ settings: { ...s.settings, ...patch } }));
    persist(saveSettings(patch));
  },

  visit: (tab, path) => {
    const { continuity } = get();
    if (continuity.lastPath === path && continuity.tabPaths[tab] === path) return;
    const next = { lastPath: path, tabPaths: { ...continuity.tabPaths, [tab]: path } };
    set({ continuity: { ...continuity, ...next } });
    persist(saveContinuity(next));
  },

  rememberScroll: (path, top) => {
    const { continuity } = get();
    const scroll = { ...continuity.scroll, [path]: Math.round(top) };
    set({ continuity: { ...continuity, scroll } });
    clearTimeout(scrollTimer);
    scrollTimer = setTimeout(() => persist(saveContinuity({ scroll: get().continuity.scroll })), 500);
  },

  showToast: (toast) => {
    const id = ++toastSeq;
    // One toast at a time: a new result replaces the previous one.
    set({ toasts: [{ ...toast, id }] });
    return id;
  },

  dismissToast: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),

  setBuilder: (source, goal, forStyle) => {
    const builder = { source, goal, forStyle };
    set((s) => ({ continuity: { ...s.continuity, builder } }));
    persist(saveContinuity({ builder }));
  },

  setTempoBpm: (tempoBpm) => {
    set((s) => ({ continuity: { ...s.continuity, tempoBpm } }));
    persist(saveContinuity({ tempoBpm }));
  },

  setCheckoutSeen: (checkoutSeen) => {
    set((s) => ({ continuity: { ...s.continuity, checkoutSeen } }));
    persist(saveContinuity({ checkoutSeen }));
  },

  setDroneLevel: (droneLevel) => {
    set((s) => ({ continuity: { ...s.continuity, droneLevel } }));
    persist(saveContinuity({ droneLevel }));
  },

  setTuner: (note, oct) => {
    const tuner = { note, oct };
    set((s) => ({ continuity: { ...s.continuity, tuner } }));
    persist(saveContinuity({ tuner }));
  },

  setEqTrainer: (eqTrainer, immediate = false) => {
    set((s) => ({ continuity: { ...s.continuity, eqTrainer } }));
    clearTimeout(eqTimer);
    if (immediate) persist(saveContinuity({ eqTrainer }));
    else eqTimer = setTimeout(() => persist(saveContinuity({ eqTrainer: get().continuity.eqTrainer })), 400);
  },

  setCompTrainer: (compTrainer, immediate = false) => {
    set((s) => ({ continuity: { ...s.continuity, compTrainer } }));
    clearTimeout(compTimer);
    if (immediate) persist(saveContinuity({ compTrainer }));
    else compTimer = setTimeout(() => persist(saveContinuity({ compTrainer: get().continuity.compTrainer })), 400);
  },

  setLibrary: (library) => {
    set((s) => ({ continuity: { ...s.continuity, library } }));
    persist(saveContinuity({ library }));
  },
}));

export const useStyleId = () => useAppStore((s) => s.settings.styleId);
